import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import EventNoticeModal from '../../components/admin/EventNoticeModal';
import Modal from '../../components/common/Modal';

const EventList = () => {
    const { get, del } = useApi();
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [itemToDelete, setItemToDelete] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            const eventData = await get('/api/events/notices/');
            setEvents(eventData);
        } catch (error) {
            console.error('Error fetching event data:', error);
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDelete = (id) => {
        setItemToDelete(id);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            await del(`/api/events/notices/${itemToDelete}/`);
            setItemToDelete(null);
            fetchData();
        } catch (error) {
            console.error('Error deleting event:', error);
        }
    };

    const openEditModal = (event) => {
        setEditingEvent(event);
        setShowModal(true);
    };

    if (loading) return <div className="py-20 text-center animate-pulse text-[10px] font-black text-slate-300 uppercase tracking-widest">Scanning School Calendar...</div>;

    return (
        <div className="space-y-10">
            <header className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black text-[#18216D]">Notice Board</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1">Broadcast school events & administrative alerts</p>
                </div>
                <button
                    onClick={() => { setEditingEvent(null); setShowModal(true); }}
                    className="px-8 py-4 bg-[#18216D] text-white rounded-[1.5rem] shadow-xl shadow-indigo-900/10 font-black text-[10px] uppercase tracking-[0.2em] hover:scale-105 transition-all active:scale-95"
                >
                    Create New Notice
                </button>
            </header>

            <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-50 overflow-hidden">
                <table className="w-full text-left">
                    <thead className="bg-slate-50/50">
                        <tr>
                            <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Title & Venue</th>
                            <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Schedule</th>
                            <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Audience</th>
                            <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">Fee</th>
                            <th className="px-8 py-6 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {events.map(event => (
                            <tr key={event.id} className="hover:bg-slate-50/50 transition-colors group">
                                <td className="px-8 py-6 max-w-sm">
                                    <div className="flex items-start gap-4">
                                        <div className={`mt-0.5 h-10 w-10 rounded-[1rem] flex-shrink-0 flex items-center justify-center text-sm shadow-sm border border-white transition-transform group-hover:scale-110 ${event.has_fee ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'}`}>
                                            <i className={`fas ${event.has_fee ? 'fa-ticket' : 'fa-bullhorn'}`}></i>
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <p className="font-black text-[#18216D] text-[13px] uppercase tracking-tight">{event.title}</p>
                                            <div className="flex items-center gap-1.5 mt-1 text-slate-400">
                                                <i className="fas fa-location-dot text-[9px] opacity-70 flex-shrink-0"></i>
                                                <p className="text-[10px] font-bold truncate tracking-wide">{event.location || 'Premises / Remote'}</p>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-8 py-6 min-w-[200px]">
                                    <div className="flex flex-col">
                                        <span className="text-[11px] font-black text-[#18216D] tracking-tight">{new Date(event.start_datetime).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' })} • {new Date(event.start_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                        <span className="text-[9px] font-bold text-slate-400 mt-1 tracking-wide flex items-center gap-1">
                                            <i className="fas fa-arrow-right text-[8px] opacity-50"></i> {new Date(event.end_datetime).toLocaleDateString([], { month: 'short', day: 'numeric' })} • {new Date(event.end_datetime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${event.target_type === 'all' ? 'bg-indigo-50 text-indigo-700 border-indigo-100' :
                                        event.target_type === 'grades' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' :
                                            'bg-purple-50 text-purple-700 border-purple-100'
                                        }`}>
                                        {event.target_type === 'all'
                                            ? 'School-Wide'
                                            : event.target_type === 'grades'
                                                ? (event.target_grade_names && event.target_grade_names.length > 0 ? event.target_grade_names.join(', ') : 'Specific Grades')
                                                : (event.target_club_names && event.target_club_names.length > 0 ? event.target_club_names.join(', ') : 'Club Specific')
                                        }
                                    </span>
                                </td>
                                <td className="px-8 py-6">
                                    {event.has_fee ? (
                                        <span className="text-[13px] font-black text-amber-600 tracking-tight whitespace-nowrap">KES {event.cost}</span>
                                    ) : (
                                        <span className="text-[9px] font-black text-slate-300 uppercase tracking-widest font-italic">No Charge</span>
                                    )}
                                </td>
                                <td className="px-8 py-6 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <button onClick={() => openEditModal(event)} className="h-10 w-10 flex items-center justify-center text-slate-300 hover:text-[#18216D] hover:bg-white rounded-xl transition-all hover:shadow-md border border-transparent hover:border-slate-100">
                                            <i className="fas fa-pen-to-square text-xs"></i>
                                        </button>
                                        <button onClick={() => handleDelete(event.id)} className="h-10 w-10 flex items-center justify-center text-slate-300 hover:text-rose-600 hover:bg-white rounded-xl transition-all hover:shadow-md border border-transparent hover:border-slate-100">
                                            <i className="fas fa-trash-can text-xs"></i>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {events.length === 0 && (
                    <div className="py-20 text-center">
                        <p className="text-slate-300 font-black uppercase tracking-[0.2em] text-[10px] italic">No active notices found in registry</p>
                    </div>
                )}
            </div>

            {showModal && (
                <EventNoticeModal
                    event={editingEvent}
                    onClose={() => setShowModal(false)}
                    onSuccess={fetchData}
                />
            )}

            <Modal
                isOpen={!!itemToDelete}
                onClose={() => setItemToDelete(null)}
                title="Confirm Deletion"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDelete}
                            className="px-8 py-3 bg-rose-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-rose-700 transition-colors shadow-lg shadow-rose-900/20"
                        >
                            Confirm Delete
                        </button>
                        <button
                            onClick={() => setItemToDelete(null)}
                            className="px-8 py-3 bg-slate-100 text-slate-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-colors"
                        >
                            Cancel
                        </button>
                    </>
                }
            >
                <div className="space-y-4">
                    <p>Are you sure you want to permanently delete this event notice? This action cannot be undone.</p>
                    <div className="p-4 bg-rose-50 rounded-2xl border border-rose-100/50">
                        <p className="text-rose-600 text-sm font-bold flex items-center gap-2">
                            <i className="fas fa-exclamation-triangle"></i>
                            Critical System Action
                        </p>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default EventList;
